import React, { useEffect, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';

import WebsiteStepLinks from '@components/Publisher/Websites/Create/WebsiteStepLinks/WebsiteStepLinks';

import Avatar from '@uikit/Avatar/Avatar';
import ColoredLabel from '@uikit/old/ColoredLabel/ColoredLabel';
import NumberInput from '@uikit/Form/NumberInput/NumberInput';
import SlideToggler from '@uikit/SlideToggler/SlideToggler';
import { Button } from '@uikit/Form/Button/Button';
import { updatePublisherWebsiteApi } from '@api/publishers.api';
import { redirectUserTo } from '@utils/historyHandler';
import urls from '@constants/urls';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import GaussianHat from '@components/Publisher/Websites/Create/GaussianHat/GaussianHat';
import activateDraftWebsite from '@components/Publisher/Websites/Create/activateDraftWebsite';
import { PublishersWebsiteDetailsType } from '@ts/publishers.types';
import { formatMetrics } from '@helpers/opportunitiesHelpers';
import { SVGIcon } from '@uikit/old/Icon/Icon';
import Loader from '@uikit/old/Loader/Loader';
import { PUBLISHER_MAX_LINKS_PER_GUEST_POST } from '@constants/publishers.constants';

import './WebsiteStepTwo.css';
import { ErrorBoundary } from '@sentry/react';

/** A typed whole number, or null while the field is empty / not a number (the field keeps its value). */
const toWholeNumber = (raw: number | ''): number | null => {
  const parsed = parseInt(String(raw), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

function WebsiteStepTwo({
  website,
  updateWebsite,
}: {
  website: PublishersWebsiteDetailsType;
  updateWebsite: (website: PublishersWebsiteDetailsType) => void;
}): JSX.Element {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const history = useHistory();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const [ordersNumber, setOrdersNumber] = useState<number>(0);
  const [guestPostLinkLimit, setGuestPostLinkLimit] = useState<number | null>(null);
  const [guestPostLinkPrice, setGuestPostLinkPrice] = useState<number>(0);
  const [guestPostTotalPrice, setGuestPostTotalPrice] = useState<number>(0);
  const [linkInsertionEnabled, setLinkInsertionEnabled] = useState<boolean>(false);
  const [guestPostEnabled, setGuestPostEnabled] = useState<boolean>(false);
  const [linkInsertionPrice, setLinkInsertionPrice] = useState<number>(25);
  const [reason, setReason] = useState<string | null>(null);
  const [isOverprice, setOverprice] = useState(false);
  const [isEarningsBusy, setIsEarningsBusy] = useState<boolean>(false);
  const earningsBusyTimeoutRef = useRef<number | null>(null);

  const triggerEarningsBusy = () => {
    setIsEarningsBusy(true);
    if (earningsBusyTimeoutRef.current) {
      window.clearTimeout(earningsBusyTimeoutRef.current);
    }
    earningsBusyTimeoutRef.current = window.setTimeout(() => {
      setIsEarningsBusy(false);
      earningsBusyTimeoutRef.current = null;
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (earningsBusyTimeoutRef.current) {
        window.clearTimeout(earningsBusyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (website != null) {
      setGuestPostLinkLimit(website.guestPostLinkLimit);
      setLinkInsertionPrice(website.linkInsertionPrice);
      setLinkInsertionEnabled(website.linkInsertionEnabled);
      setGuestPostEnabled(website.guestPostEnabled);
      const total = website.guestPostLinkPrice * (website.guestPostLinkLimit || 1);
      const roundedTotal = Math.round(total);
      setGuestPostTotalPrice(roundedTotal);
      setGuestPostLinkPrice(roundedTotal / (website.guestPostLinkLimit || 1));
      setIsLoading(false);
    }
  }, [website]);

  const delayedUpdate = useRef(
    debounce((params) => {
      if (
        params.website.guestPostLinkLimit === params.guestPostLinkLimit &&
        params.website.linkInsertionPrice === params.linkInsertionPrice &&
        params.website.guestPostLinkPrice === params.guestPostLinkPrice &&
        params.website.guestPostEnabled === params.guestPostEnabled &&
        params.website.linkInsertionEnabled === params.linkInsertionEnabled
      ) {
        return;
      }

      updatePublisherWebsiteApi(
        currentWorkspaceId,
        params.website.id,
        params.guestPostEnabled,
        params.guestPostLinkLimit,
        params.guestPostLinkPrice,
        params.linkInsertionEnabled,
        params.linkInsertionPrice,
        params.website.turnaroundTime,
        params.website.notes,
        params.website.typesList,
        params.website.verticalsList,
        params.website.restrictedPricesList,
        params.website.languagesList,
        params.website.articleWordsCountMin,
        params.website.articleWordsCountMax
      ).then(updateWebsite);
    }, 400)
  ).current;

  useEffect(() => {
    if (!website || isLoading) {
      return;
    }

    delayedUpdate({
      website,
      guestPostLinkLimit,
      guestPostLinkPrice,
      guestPostEnabled,
      linkInsertionEnabled,
      linkInsertionPrice,
    });
  }, [
    linkInsertionPrice,
    linkInsertionEnabled,
    guestPostEnabled,
    guestPostLinkLimit,
    guestPostLinkPrice,
  ]);

  useEffect(() => {
    if (!isLoading && website != null) {
      const limit = guestPostLinkLimit || 1;
      const total = guestPostLinkPrice * limit;
      setGuestPostTotalPrice(Math.round(total));
    }
  }, [guestPostLinkLimit]);

  const drValue = Number(formatMetrics(website?.metrics?.ahrefRating)) || 0;

  const dynamicRecommendedPrice = website
    ? Math.round(website.guestPostLinkInitPrice * (guestPostLinkLimit || 1)) || 1
    : 1;

  const getPeakOrders = () => {
    let defaultNumberLinks = 3;

    if (drValue >= 60) {
      defaultNumberLinks = 2;
    } else if (drValue >= 50) {
      defaultNumberLinks = 2;
    } else if (drValue >= 30) {
      defaultNumberLinks = 3;
    } else if (drValue >= 20) {
      defaultNumberLinks = 3;
    } else if (drValue >= 10) {
      defaultNumberLinks = 3;
    }

    return {
      peakOrders: Math.max(defaultNumberLinks, 1),
      defaultNumberLinks: Math.max(defaultNumberLinks, 1),
    };
  };

  const { defaultNumberLinks, peakOrders } = getPeakOrders();

  useEffect(() => {
    if (!guestPostLinkLimit && !website?.guestPostLinkLimit) {
      setGuestPostLinkLimit(defaultNumberLinks);
    }
  }, [guestPostLinkPrice, guestPostLinkLimit]);

  const handleContinue = () => {
    // Continue saves what the form shows: the local toggle, not the last saved value, and a pending
    // autosave must not land after it with older values.
    delayedUpdate.cancel();
    setIsLoading(true);
    updatePublisherWebsiteApi(
      currentWorkspaceId,
      website.id,
      guestPostEnabled,
      guestPostLinkLimit,
      guestPostLinkPrice,
      linkInsertionEnabled,
      linkInsertionPrice,
      website.turnaroundTime,
      website.notes,
      website.typesList,
      website.verticalsList,
      website.restrictedPricesList,
      website.languagesList,
      website.articleWordsCountMin,
      website.articleWordsCountMax
    )
      .then((response) => activateDraftWebsite(currentWorkspaceId, response))
      .then((response) => {
        updateWebsite(response);
        redirectUserTo(history, `${urls.WEBSITE_STEP_THREE(currentWorkspaceId, response.id)}`);
      })
      .finally(() => setIsLoading(false));
  };

  const calculateEstimatedEarnings = () => {
    // keep calculations precise (no rounding here); round only for display
    if (!guestPostEnabled) {
      return ordersNumber * linkInsertionPrice;
    }
    return ordersNumber * guestPostTotalPrice;
  };

  const calculatePeakEstimatedEarnings = () => {
    return peakOrders * dynamicRecommendedPrice;
  };

  const getOrders = () => {
    if (Math.ceil(ordersNumber) === ordersNumber) {
      return ordersNumber;
    }

    if (ordersNumber > peakOrders) {
      return `more than ${Math.floor(ordersNumber)}`;
    }

    return `less than ${Math.ceil(ordersNumber)}`;
  };

  const handleResetToInitial = () => {
    const initLimit = website?.guestPostLinkInitLimit || 1;
    const initTotal =
      (website?.guestPostLinkInitPrice || 0) * (website?.guestPostLinkInitLimit || 1);
    setGuestPostLinkLimit(initLimit);
    setGuestPostLinkPrice(initTotal / initLimit);
    setGuestPostTotalPrice(Math.round(initTotal));
    setOverprice(false);
  };

  if (!website || isLoading) {
    return <Loader isFullScreenLoading />;
  }

  return (
    <div className="website-step" data-testid="website-wizard-step-2">
      <WebsiteStepLinks id={website?.id} isDisabledStep={false} step={2} />
      <div className="website-step-container step-2">
        <h2>Set your price</h2>
        <p>Set your price for guest posts and link insertions.</p>
        <p>Maximize your earnings by choosing the recommended price.</p>

        <div className="website-step-container__calculation">
          <div className="website-step-container__calculation-item">
            <div className="item-container">
              <div className="flex-content">
                <SlideToggler
                  value={guestPostEnabled}
                  testId="website-wizard-guest-post-toggle"
                  onChange={() => {
                    const newValue = !guestPostEnabled;
                    setGuestPostEnabled(newValue);
                    if (!newValue) {
                      setOrdersNumber(1);
                      setOverprice(false);
                    }
                  }}
                />
                <p>Guest post price</p>
              </div>
              <span>Recommended: ${dynamicRecommendedPrice}</span>
            </div>
            <NumberInput
              isCustom
              value={Math.round(guestPostTotalPrice)}
              data-testid="website-wizard-guest-post-price"
              maxNumber={9999}
              minNumber={1}
              onChange={(e) => {
                if (!guestPostEnabled) {
                  return;
                }
                const total = toWholeNumber(e);
                if (total === null) {
                  return;
                }
                const limit = guestPostLinkLimit || 1;
                setGuestPostTotalPrice(total);
                setGuestPostLinkPrice(total / limit);
                triggerEarningsBusy();
              }}
              disabled={!guestPostEnabled}
              disabledArrows={!guestPostEnabled}
            />
          </div>

          <div className="website-step-container__calculation-item">
            <div className="item-container">
              <p>Max # of dofollow links per guest post</p>
              <span>Recommended: {website.guestPostLinkInitLimit}</span>
            </div>
            <NumberInput
              isCustom
              value={guestPostLinkLimit}
              data-testid="website-wizard-link-limit"
              maxNumber={PUBLISHER_MAX_LINKS_PER_GUEST_POST}
              minNumber={1}
              onChange={(e) => {
                if (!guestPostEnabled) {
                  return;
                }
                const newLimit = toWholeNumber(e);
                if (newLimit === null) {
                  return;
                }
                setGuestPostLinkLimit(newLimit);
                setGuestPostTotalPrice(Math.round(guestPostLinkPrice * newLimit));
                triggerEarningsBusy();
              }}
              disabled={!guestPostEnabled}
              disabledArrows={!guestPostEnabled}
            />
          </div>

          <div className="website-step-container__calculation-item">
            <div className="item-container">
              <div className="flex-content">
                <SlideToggler
                  value={linkInsertionEnabled}
                  testId="website-wizard-link-insertion-toggle"
                  onChange={() => setLinkInsertionEnabled(!linkInsertionEnabled)}
                />
                <p>Link insertion price</p>
              </div>
              <span>Recommended: ${website.guestPostLinkInitPrice}</span>
            </div>
            <NumberInput
              isCustom
              beforeText="$"
              value={linkInsertionPrice}
              data-testid="website-wizard-link-insertion-price"
              maxNumber={9999}
              minNumber={1}
              onChange={(e) => {
                if (!linkInsertionEnabled) {
                  return;
                }
                const price = toWholeNumber(e);
                if (price === null) {
                  return;
                }
                setLinkInsertionPrice(price);
              }}
              disabled={!linkInsertionEnabled}
              disabledArrows={!linkInsertionEnabled}
            />
          </div>
        </div>

        <div className="website-step-container__price">
          <div
            className={`website-step-container__price-values price-busy-wrap ${
              isEarningsBusy ? 'is-busy' : ''
            }`}
            aria-busy={isEarningsBusy}
          >
            <div className="price-values-container">
              <p className="price-label">Estimate earnings</p>
              <span className="price-subtitle">
                ~
                {isOverprice
                  ? `0 orders at $${Math.round(guestPostEnabled ? guestPostTotalPrice : linkInsertionPrice)}+`
                  : `${getOrders()} order${Math.ceil(ordersNumber) > 1 ? 's' : ''} at $${Math.round(
                      guestPostEnabled ? guestPostTotalPrice : linkInsertionPrice
                    )}`}
              </span>
            </div>
            <div className="price-value" aria-live="polite">
              ${Math.round(calculateEstimatedEarnings())}
              <span>/ mo</span>
            </div>
            {isEarningsBusy && (
              <div className="price-busy-overlay" aria-hidden="true">
                <div className="price-busy-spinner" />
              </div>
            )}
          </div>
        </div>

        <div className="website-step-container__graph">
          {(guestPostEnabled || linkInsertionEnabled) && (guestPostEnabled ? guestPostLinkPrice > 0 : linkInsertionPrice > 0) && peakOrders ? (
            <ErrorBoundary>
              <GaussianHat
                isGuestPost={guestPostEnabled}
                // Keep the Gaussian curve static by basing it on the initial website price
                // instead of the live slider value (guestPostPrice).
                recommendedPrice={guestPostEnabled ? dynamicRecommendedPrice : website.guestPostLinkInitPrice}
                guestPostPrice={Math.round(guestPostEnabled ? guestPostTotalPrice : linkInsertionPrice)}
                guestPostLinkLimit={guestPostEnabled ? (guestPostLinkLimit || 1) : 1}
                topValue={guestPostEnabled ? calculatePeakEstimatedEarnings() : (peakOrders * website.guestPostLinkInitPrice)}
                setOrdersNumber={setOrdersNumber}
                setRequestedPrice={(price: number) => {
                  if (guestPostEnabled) {
                    const limit = guestPostLinkLimit || 1;
                    setGuestPostTotalPrice(price);
                    setGuestPostLinkPrice(price / limit);
                  } else {
                    setLinkInsertionPrice(price);
                  }
                  triggerEarningsBusy();
                }}
                peakOrders={peakOrders}
                setOverprice={setOverprice}
                onResetToInitial={guestPostEnabled ? handleResetToInitial : () => {
                  setLinkInsertionPrice(website.guestPostLinkInitPrice);
                  setOverprice(false);
                }}
              />
            </ErrorBoundary>
          ) : null}
          <div className="website-step-container__trafic">
            <div className="website-step-container__trafic-header">
              <Avatar isSquared url={website.logo} fullName={website.name} size="xlsx" />
              <p>{website.name}</p>
              {reason?.length > 0 && (
                <>
                  <span
                    style={{ cursor: 'pointer' }}
                    data-for={`traffic-header-tooltip-${website.id}`}
                    data-tip=""
                  >
                    <SVGIcon icon="questionMark" />
                  </span>
                  <ReactTooltip
                    id={`traffic-header-tooltip-${website.id}`}
                    place="top"
                    effect="solid"
                    arrowColor="transparent"
                  >
                    <p style={{ color: '#fff' }}>{reason}</p>
                  </ReactTooltip>
                </>
              )}
            </div>
            <div className="website-step-container__trafic-block">
              {website?.metrics?.ahrefRating === -1 ? (
                '-'
              ) : (
                <ColoredLabel color="dark-green" isSquared>
                  <span>DR {drValue}</span>
                </ColoredLabel>
              )}
              <div className="trafic-value">
                <span>Montly Traffic</span>
                <p>{formatMetrics(website?.metrics?.traffic)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="website-step-container__footer">
          <Button onClick={handleContinue} data-testid="website-wizard-next">
            Continue
          </Button>
        </div>
      </div>
      <div className="website-step-container__dammy" />
    </div>
  );
}

export default WebsiteStepTwo;
